import React, { useState, useEffect } from "react";
import {
  Table,
  Button,
  Input,
  Select,
  DatePicker,
  Form,
  Row,
  Col,
  Card,
  Typography,
  message,
  Modal,
} from "antd";
import axiosInstance from '../../utils/axiosInstance';
import dayjs from "dayjs";

const { Option } = Select;
const { Title } = Typography;

const BidHistory = () => {
  const [searchText, setSearchText] = useState("");
  const [form] = Form.useForm();
  const [editForm] = Form.useForm();
  const [gameOptions, setGameOptions] = useState([]);
  const [bidData, setBidData] = useState([]);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingBid, setEditingBid] = useState(null);
  const [pageSize, setPageSize] = useState(10);
  const [loading, setLoading] = useState(false);

  // 1. Filter Function
  const handleFilterBids = async () => {
    setLoading(true);
    try {
      const values = await form.validateFields();
      
      const payload = {
        date: values.date ? dayjs(values.date).format("DD-MM-YYYY") : "",
        gamename: values.gameName ? values.gameName.split(" [")[0].trim() : "",
        gametype: "", // Send empty to fetch ALL types
        market: "Jackpot",
      };

      console.log("🚀 Sending Payload:", payload);

      const accessToken = localStorage.getItem("accessToken");
      const response = await axiosInstance.post(
        "/api/jackpotBid/filterBids",
        payload,
        {
          headers: { Authorization: `Bearer ${accessToken}` },
        }
      );

      const fetchedBids = response.data.bids || response.data.data || [];

      if (response.data.success) {
        const bidsWithKey = fetchedBids.map((bid, index) => ({
          ...bid,
          key: bid._id || index,
        }));
        setBidData(bidsWithKey);
      } else {
        setBidData([]);
      }
    } catch (error) {
      console.error("Filter Error:", error);
    } finally {
      setLoading(false);
    }
  };

  // 2. Initial Load
  useEffect(() => {
    const init = async () => {
      form.setFieldsValue({ date: dayjs() });

      try {
        const accessToken = localStorage.getItem("accessToken");
        const response = await axiosInstance.get("/api/jackpotMarket/getAllMarket", {
          headers: { Authorization: `Bearer ${accessToken}` },
        });
        if (response.data && Array.isArray(response.data.data)) {
          setGameOptions(response.data.data);
        }
      } catch (error) {
        console.error("Error fetching games");
      }

      handleFilterBids();
    };

    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form]); 

  // Edit Modal
  const openEditModal = (record) => {
    setEditingBid(record);
    editForm.setFieldsValue({
      points: record.points,
      newDigit: record.digit || record.pana,
    });
    setIsEditModalOpen(true);
  };

  // Update Bid
  const handleUpdateBid = async () => {
    try {
      const values = await editForm.validateFields();
      const payload = {
        points: values.points,
        leftdigit: "false",
        rightdigit: "false",
        pana: values.newDigit,
      };

      const accessToken = localStorage.getItem("accessToken");
      const idToUpdate = editingBid.bidId || editingBid._id;

      await axiosInstance.put(
        `/api/jackpotBid/updateBid/${idToUpdate}`,
        payload,
        {
          headers: { Authorization: `Bearer ${accessToken}` },
        }
      );
      
      message.success("Bid updated successfully");
      setIsEditModalOpen(false);
      setEditingBid(null);
      handleFilterBids(); 
    } catch (error) {
      message.error("Error updating bid");
    }
  };

  // Delete Bid
  const handleDeleteBid = (bidId) => {
    Modal.confirm({
      title: 'Are you sure you want to delete this bid?',
      content: 'This action cannot be undone.',
      okText: 'Yes, Delete',
      okType: 'danger',
      cancelText: 'No',
      onOk: async () => {
        try {
          const accessToken = localStorage.getItem("accessToken");
          await axiosInstance.delete(`/api/jackpotBid/deleteBid/${bidId}`, {
            headers: { Authorization: `Bearer ${accessToken}` },
          });
          message.success("Bid deleted successfully");
          setBidData(prev => prev.filter(item => item.bidId !== bidId && item._id !== bidId));
        } catch (error) {
          message.error("Error deleting bid");
        }
      }
    });
  };

  // ✅ Updated Columns: Removed Email, Kept User Name
  const columns = [
    {
      title: "#",
      render: (_, __, index) => index + 1,
      width: 50,
    },
    { 
      title: "User Name", 
      dataIndex: "userName", 
      key: "userName",
      render: (text) => <span style={{ fontWeight: "bold", textTransform: "capitalize" }}>{text || "Unknown"}</span>
    },
    // ❌ Email Column Removed
    { title: "Bid TXID", dataIndex: "bidId", key: "bidId" },
    { title: "Game Name", dataIndex: "gamename", key: "gamename" },
    { title: "Game Type", dataIndex: "gametype", key: "gametype" },
    { 
      title: "Digit", 
      dataIndex: "digit", 
      key: "digit", 
      render: (text, record) => (
        <span style={{ color: "#1890ff", fontWeight: "bold" }}>
          {text || record.pana || "-"}
        </span>
      )
    },
    { 
      title: "Points", 
      dataIndex: "points", 
      key: "points",
      render: (points) => <span style={{ fontWeight: 'bold', color: 'green' }}>{points}</span>
    },
    {
      title: "Action",
      key: "action",
      render: (_, record) => (
        <>
          <Button type="primary" size="small" onClick={() => openEditModal(record)} style={{ marginRight: 8 }}>
            Edit
          </Button>
          <Button type="primary" danger size="small" onClick={() => handleDeleteBid(record.bidId || record._id)}>
            Delete
          </Button>
        </>
      ),
    },
  ];

  // Search Logic (Updated to ignore email)
  const filteredBidData = bidData.filter((item) => {
    const term = searchText.toLowerCase();
    return (
      (item.userName && item.userName.toLowerCase().includes(term)) ||
      (item.bidId && item.bidId.toLowerCase().includes(term))
    );
  });

  return (
    <div style={{ padding: 20 }}>
      <Title level={2} style={{ textAlign: "center", marginBottom: 30 }}>Jackpot Bid History</Title>

      <Card style={{ marginBottom: 20, borderRadius: 10, boxShadow: "0 2px 8px rgba(0,0,0,0.1)" }}>
        <Form form={form} layout="vertical">
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12} md={6}>
              <Form.Item label="Date" name="date" rules={[{ required: true, message: 'Select Date' }]}>
                <DatePicker style={{ width: "100%" }} format="DD-MM-YYYY" allowClear={false} />
              </Form.Item>
            </Col>
            
            <Col xs={24} sm={12} md={6}>
              <Form.Item label="Game Name" name="gameName">
                <Select
                  placeholder="Select Game (Optional)"
                  showSearch
                  optionFilterProp="label"
                  allowClear
                >
                  {gameOptions.map((game) => (
                    <Option
                      key={game._id}
                      value={game.game_name}
                      label={game.game_name}
                    >
                      {`${game.game_name} [ ${dayjs(game.open_time, "hh:mm:ssA").format("hh:mm A")} ]`}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            
            <Col xs={24} sm={12} md={6}>
              <Form.Item label="Game Type" name="gameType" initialValue="Jodi Digit">
                <Select disabled>
                  <Option value="Jodi Digit">Jodi Digit</Option>
                </Select>
              </Form.Item>
            </Col>
            
            <Col xs={24} sm={12} md={6}>
              <Form.Item label=" ">
                <Button type="primary" onClick={handleFilterBids} loading={loading} block style={{ marginTop: 4 }}>
                  Search Bids
                </Button>
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Card>

      <Card style={{ borderRadius: 10, boxShadow: "0 2px 8px rgba(0,0,0,0.1)" }}>
        <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
          <Col>
            <Select defaultValue={pageSize} style={{ width: 80 }} onChange={setPageSize}>
              <Option value={5}>5</Option>
              <Option value={10}>10</Option>
              <Option value={20}>20</Option>
              <Option value={50}>50</Option>
            </Select>
            <span style={{ marginLeft: 8 }}>entries per page</span>
          </Col>
          <Col>
            <Input
              placeholder="Search User Name or ID..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              style={{ width: 250 }}
              allowClear
            />
          </Col>
        </Row>

        <Table
          columns={columns}
          dataSource={filteredBidData}
          pagination={{ pageSize, showTotal: (total) => `Total ${total} bids` }}
          rowKey="key"
          loading={loading}
          scroll={{ x: 800 }}
          bordered
        />
      </Card>

      <Modal
        title="Edit Jodi Bid"
        open={isEditModalOpen}
        onOk={handleUpdateBid}
        onCancel={() => {
          setIsEditModalOpen(false);
          editForm.resetFields();
          setEditingBid(null);
        }}
        okText="Update"
        confirmLoading={loading}
      >
        <Form form={editForm} layout="vertical">
          <Form.Item name="points" label="Bid Points" rules={[{ required: true, message: 'Points required' }]}>
            <Input type="number" />
          </Form.Item>
          <Form.Item label="Game Type">
            <Input value="Jodi Digit" disabled />
          </Form.Item>
          <Form.Item name="newDigit" label="Digit (Jodi)" rules={[{ required: true, message: 'Digit required' }]}>
            <Input type="number" maxLength={2} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default BidHistory;